// "Descrever com IA" (espec. 10.3): texto livre em português vira uma PROPOSTA de
// alvo + perfil — nunca uma gravação. Um único candidato preenche o editor na hora;
// dois ou mais abrem um seletor e só preenchem no clique. O `Salvar` do editor
// continua sendo o único jeito de gravar (contrato da Task 11/BE Task 6).
import { useEffect, useRef, useState } from "react";

import { money } from "../format";
import { describeRuleWithAi, newIdempotencyKey } from "./disbursementAiApi";

const NO_CANDIDATE_MESSAGE =
  "Não encontrei um alvo para essa descrição. Tente citar o insumo ou o grupo.";
const GENERIC_ERROR_MESSAGE = "A IA está temporariamente indisponível.";
const PROMPT_MAX_LENGTH = 500;
// Only unresolved create identities survive a widget remount; no payload is stored.
const pendingCreates = new Map();
export const discardPendingDescriptionRequest = (workId) =>
  pendingCreates.delete(workId);

/**
 * Valor previsto do candidato, isolado em componente próprio pela mesma razão do
 * `TargetValue` no `RuleTargetPicker`: `money()` chama um hook disfarçado
 * (`useProjectContext`), e cada item da lista precisa da sua PRÓPRIA instância para
 * não mudar a contagem de hooks do componente hospedeiro conforme os candidatos mudam.
 */
const CandidatePlanned = ({ cents }) => <span>{money(cents)}</span>;

const DescribeRuleWithAi = ({ workId, targetKey, onFill }) => {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null);
  const [message, setMessage] = useState(null);
  const [processing, setProcessing] = useState(false);
  const requestVersion = useRef(0);
  useEffect(() => {
    requestVersion.current += 1;
    setDraft(null);
    setLoading(false);
    setMessage(null);
    setProcessing(false);
    return () => {
      requestVersion.current += 1;
    };
  }, [workId, targetKey]);

  const fillFrom = (candidate, current) => {
    onFill?.({
      target: {
        ...candidate,
        plannedCents: candidate.plannedCents ?? null,
        ...(candidate.ruleLevel ? { level: candidate.ruleLevel } : {}),
      },
      profile: current.payload.profile,
      draftId: current.draftId,
      version: current.version,
    });
    setDraft(null);
  };

  const signature = JSON.stringify([targetKey ?? null, prompt.trim()]);
  const pending = pendingCreates.get(workId)?.signature === signature;
  const generate = (fresh = false) => {
    const description = prompt.trim();
    if (!description || loading) return;
    setLoading(true);
    setMessage(null);
    setDraft(null);
    setProcessing(false);
    requestVersion.current += 1;
    const version = requestVersion.current;
    if (fresh || !pending)
      pendingCreates.set(workId, { signature, key: newIdempotencyKey() });
    const request = pendingCreates.get(workId);
    describeRuleWithAi(
      workId,
      { prompt: description },
      { idempotencyKey: request.key },
    )
      .then((result) => {
        if (version !== requestVersion.current) return;
        if (result?.status === "processing") {
          setProcessing(true);
          return;
        }
        if (pendingCreates.get(workId) === request)
          pendingCreates.delete(workId);
        if (result?.status === "cannot_propose" || !result?.draft) {
          setMessage(NO_CANDIDATE_MESSAGE);
          return;
        }
        const candidates = result.draft.payload?.targetCandidates ?? [];
        if (candidates.length === 0) {
          setMessage(NO_CANDIDATE_MESSAGE);
          return;
        }
        if (candidates.length === 1) {
          fillFrom(candidates[0], result.draft);
          return;
        }
        setDraft(result.draft);
      })
      .catch((requestError) => {
        if (version !== requestVersion.current) return;
        setMessage(requestError?.message ?? GENERIC_ERROR_MESSAGE);
      })
      .finally(() => {
        if (version === requestVersion.current) setLoading(false);
      });
  };

  const candidates = draft?.payload?.targetCandidates ?? [];

  return (
    <div className="ampli-rule-editor__describe-ai">
      <label className="ampli-rule-editor__field">
        <span className="card-label">Descrever com IA</span>
        <textarea
          aria-label="Descrever com IA"
          value={prompt}
          maxLength={PROMPT_MAX_LENGTH}
          disabled={loading}
          onChange={(event) => setPrompt(event.target.value)}
          placeholder="Ex.: elevador paga 30% três meses antes e o resto em quatro parcelas"
        />
      </label>
      <button
        type="button"
        className="button button-secondary small"
        disabled={loading}
        onClick={() => generate()}
      >
        {pending ? "Consultar pedido" : "Gerar"}
      </button>
      {pending && !loading ? (
        <button type="button" onClick={() => generate(true)}>
          Iniciar novo pedido
        </button>
      ) : null}
      {processing ? (
        <p role="status">
          O pedido está em processamento. Consulte novamente quando quiser;
          nenhuma consulta é automática.
        </p>
      ) : null}

      {message ? (
        <p className="alert alert-error" role="alert">
          {message}
        </p>
      ) : null}

      {candidates.length > 1 ? (
        <div
          className="ampli-rule-editor__ai-candidates"
          role="group"
          aria-label="Escolha o alvo da regra"
        >
          <p>Escolha o alvo da regra</p>
          <ul>
            {candidates.map((candidate) => (
              <li key={candidate.key}>
                <button
                  type="button"
                  className="button button-secondary small"
                  onClick={() => fillFrom(candidate, draft)}
                >
                  <span>{candidate.label}</span>
                  {" · previsto "}
                  <CandidatePlanned cents={candidate.plannedCents} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export default DescribeRuleWithAi;
